import React, { useState } from 'react';
import {
  styled,
  InputBase,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import { TextMaskLicensePlate } from './TextMaskCustom';
import { useModals } from '../hooks/useModals.jsx';
import { brandTokens } from '../single-page/theme';

const RootContainer = styled('div')({
  position: 'relative',
  width: '100%',
  margin: 0,
  maxWidth: 560,
});

const SearchCard = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: brandTokens.paper,
  borderRadius: 12,
  padding: theme.spacing(3),
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
  borderTop: `4px solid ${brandTokens.green}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SearchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
}));

const B2bLink = styled('a')(({ theme }) => ({
  display: 'inline-block',
  paddingTop: theme.spacing(2),
  borderTop: '1px solid rgba(1, 47, 71, 0.12)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: 13,
  fontWeight: 500,
  color: 'rgba(1, 47, 71, 0.6)',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: brandTokens.green,
  },
}));

const FieldBlock = styled(Box)({
  flex: 1,
  width: '100%',
});

const FieldLabel = styled(Typography)({
  fontFamily: 'GT Haptik, Roboto, sans-serif',
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: brandTokens.navy,
  marginBottom: 8,
});

const InputWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  height: 56,
  backgroundColor: '#F5F7F9',
  borderRadius: 8,
  border: '2px solid #C2C7CD',
  display: 'flex',
  alignItems: 'center',
  transition: 'border-color 0.2s ease',
  '&:focus-within': {
    borderColor: brandTokens.green,
  },
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  height: '100%',
  '& input': {
    height: '100%',
    padding: '0 48px 0 16px',
    fontSize: 18,
    fontFamily: 'GT Haptik, Roboto, sans-serif',
    fontWeight: 600,
    color: brandTokens.navy,
    textTransform: 'uppercase',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: 'rgba(1, 47, 71, 0.3)',
      opacity: 1,
      fontWeight: 400,
    },
  },
}));

const CarIcon = styled(DirectionsCarOutlinedIcon)({
  position: 'absolute',
  right: 14,
  color: brandTokens.green,
  pointerEvents: 'none',
});

const SearchButton = styled(Button)(({ theme }) => ({
  background: brandTokens.signatureGradient,
  color: '#FFF',
  borderRadius: 8,
  padding: theme.spacing(0, 3),
  minWidth: 160,
  height: 56,
  textTransform: 'uppercase',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: '0.05em',
  boxShadow: '0 8px 24px rgba(81, 223, 148, 0.3)',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: brandTokens.signatureGradient,
    filter: 'brightness(1.06)',
  },
  '&.Mui-disabled': {
    background: brandTokens.signatureGradient,
    opacity: 0.5,
    color: '#ffffffcc',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export default function SearchLicensePlate() {
  const [licensePlate, setLicensePlate] = useState('');
  const [loading, setLoading] = useState(false);
  const { handleOpenPlans } = useModals();

  const isValidPlate = (plate) => {
    const cleanPlate = plate.trim().toUpperCase();
    return cleanPlate.length === 8;
  };

  const handleChange = (e) => {
    const value = e.target?.value || e;
    setLicensePlate(value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!licensePlate.trim()) return;

    const formattedPlate = licensePlate.replace('-', '');

    try {
      setLoading(true);
      handleOpenPlans(formattedPlate);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleB2bClick = (e) => {
    e.preventDefault();
    document.getElementById('b2bPackages')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <RootContainer>
      <SearchCard component="form" onSubmit={handleSearch}>
        <SearchRow>
          <FieldBlock>
            <FieldLabel>Placa do Veículo</FieldLabel>
            <InputWrapper>
              <StyledInput
                id="license_plate"
                name="license_plate"
                required
                autoComplete="off"
                value={licensePlate}
                onChange={handleChange}
                placeholder="AAA-0A00"
                inputComponent={TextMaskLicensePlate}
                inputProps={{
                  'aria-label': 'consulta de placa',
                  maxLength: 8,
                  onChange: handleChange,
                }}
              />
              <CarIcon fontSize="small" />
            </InputWrapper>
          </FieldBlock>
          <SearchButton
            type="submit"
            variant="contained"
            disabled={!isValidPlate(licensePlate) || loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Consultar agora
          </SearchButton>
        </SearchRow>
        <B2bLink href="#b2bPackages" onClick={handleB2bClick}>
          É empresa? Veja pacotes com desconto →
        </B2bLink>
      </SearchCard>
    </RootContainer>
  );
}
